import { AlertTriangle, CheckCircle, Clock, Plus, TrendingUp, X } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import LoadingSpinner from '../LoadingSpinner';
import taskService from '../../services/taskService';
import { authService } from '../../services/authService';
import { Task, TaskProgressUpdate } from '../../types';

interface TaskProgressDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  onTaskUpdated?: () => void;
}

interface ProgressUpdateForm {
  progress: number;
  status: string;
  details: string;
}

const TaskProgressDrawer: React.FC<TaskProgressDrawerProps> = ({
  isOpen,
  onClose,
  taskId,
  onTaskUpdated,
}) => {
  const [task, setTask] = useState<Task | null>(null);
  const [progressHistory, setProgressHistory] = useState<TaskProgressUpdate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showUpdateForm, setShowUpdateForm] = useState(false);

  const [updateForm, setUpdateForm] = useState<ProgressUpdateForm>({
    progress: 0,
    status: 'on_track',
    details: '',
  });

  // Progress status options
  const progressStatusOptions = [
    { value: 'on_track', label: 'On Track', color: 'text-green-600', bgColor: 'bg-green-100' },
    {
      value: 'need_attention',
      label: 'Need Attention',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
    },
    { value: 'blocked', label: 'Blocked', color: 'text-red-600', bgColor: 'bg-red-100' },
    { value: 'completed', label: 'Completed', color: 'text-blue-600', bgColor: 'bg-blue-100' },
    { value: 'delayed', label: 'Delayed', color: 'text-orange-600', bgColor: 'bg-orange-100' },
  ];

  // Load task and progress history
  const loadTaskProgress = useCallback(async () => {
    if (!taskId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Load task details
      const taskResponse = await taskService.getTask(taskId);
      if (taskResponse.success && taskResponse.data) {
        setTask(taskResponse.data);
        setUpdateForm(prev => ({
          ...prev,
          progress: taskResponse.data?.progress || 0,
        }));
      }

      // Load progress history
      const historyResponse = await taskService.getTaskProgressHistory(taskId);
      if (historyResponse.success && historyResponse.data) {
        setProgressHistory(historyResponse.data);
      }
    } catch {
      setError('An unexpected error occurred while loading task progress');
    } finally {
      setIsLoading(false);
    }
  }, [taskId]);

  // Load data when drawer opens
  useEffect(() => {
    if (isOpen && taskId) {
      loadTaskProgress();
    }
  }, [isOpen, taskId, loadTaskProgress]);

  // Handle progress update submission
  const handleProgressUpdate = async () => {
    if (!task) return;

    setIsUpdating(true);
    setError(null);

    try {
      const response = await taskService.updateTaskProgress(task.id, {
        progress: updateForm.progress,
        progressStatus: updateForm.status,
        details: updateForm.details.trim(),
        taskStatus: updateForm.progress === 100 ? 'completed' : task.status,
      });

      if (response.success) {
        setSuccessMessage('Progress updated successfully');
        setShowUpdateForm(false);
        setUpdateForm(prev => ({ ...prev, details: '' }));

        // Reload task progress to get updated history
        // Note: This will fetch the latest data from the backend
        await loadTaskProgress();
        onTaskUpdated?.();
      } else {
        setError(response.message || 'Failed to update progress');
      }
    } catch {
      setError('An unexpected error occurred while updating progress');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle mark as complete
  const handleMarkComplete = async () => {
    if (!task) return;

    setIsUpdating(true);
    setError(null);

    try {
      const response = await taskService.updateTaskProgress(task.id, {
        progress: 100,
        progressStatus: 'completed',
        details: 'Task marked as completed',
        taskStatus: 'completed',
      });

      if (response.success) {
        setSuccessMessage('Task marked as completed');
        await loadTaskProgress();
        onTaskUpdated?.();
      } else {
        setError(response.message || 'Failed to complete task');
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsUpdating(false);
    }
  };

  // Format date
  const formatDate = (date: Date | string) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  // Safe user name resolution with enhanced debugging and fallbacks
  const getUserDisplayName = (user: any): string => {
    if (!user) {
      return 'Unknown User';
    }

    // Debug: Log the user object structure (remove in production)
    if (process.env.NODE_ENV === 'development') {
      console.log('getUserDisplayName user object:', user);
    }

    // Handle string values (sometimes backend sends just a name string)
    if (typeof user === 'string') {
      return user;
    }

    // Try firstName/lastName combination (most common)
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`.trim();
    }

    // Try firstName only
    if (user.firstName) {
      return user.firstName;
    }

    // Try lastName only
    if (user.lastName) {
      return user.lastName;
    }

    // Try nested user object (sometimes data comes as user.user)
    if (user.user) {
      return getUserDisplayName(user.user);
    }

    // Try name field (full name)
    if (user.name) {
      return user.name;
    }

    // Try username field
    if (user.username) {
      return user.username;
    }

    // Try email prefix
    if (user.email) {
      return user.email.split('@')[0];
    }

    // Try _id as last resort (for debugging)
    if (user._id || user.id) {
      return `User ${user._id || user.id}`;
    }

    // Get current user as fallback for new updates
    const currentUser = authService.getStoredUser();
    if (currentUser) {
      if (currentUser.firstName || currentUser.lastName) {
        return `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim();
      }
      if (currentUser.name) {
        return currentUser.name;
      }
      if (currentUser.email) {
        return currentUser.email.split('@')[0];
      }
    }

    return 'Current User';
  };

  // Get progress status display
  const getProgressStatusDisplay = (status: string) => {
    const option = progressStatusOptions.find(opt => opt.value === status);
    return option || progressStatusOptions[0];
  };

  // Get progress icon
  const getProgressIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'blocked':
        return <AlertTriangle className="h-4 w-4" />;
      case 'need_attention':
      case 'delayed':
        return <Clock className="h-4 w-4" />;
      default:
        return <TrendingUp className="h-4 w-4" />;
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-full max-w-lg animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden brand-form">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-4 w-4 text-red-500" />
              <h2 className="text-base font-semibold text-gray-900">Task Progress Tracking</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {/* Scroll container */}
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800">{error}</p>
              </div>
            ) : task ? (
              <div className="space-y-4">
                {/* Content wrapper with reduced spacing */}
                {/* Success Message */}
                {successMessage && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-green-800">{successMessage}</p>
                      <button
                        onClick={() => setSuccessMessage(null)}
                        className="text-green-600 hover:text-green-800"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Task Overview */}
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{task.title}</h3>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{task.description}</p>

                  {/* Current Progress */}
                  <div className="bg-gray-50 rounded-lg p-3 mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-700">Current Progress</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {task.progress || 0}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-red-500 h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${task.progress || 0}%` }}
                      />
                    </div>
                    {task.progressStatus && (
                      <div className="mt-2">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getProgressStatusDisplay(task.progressStatus).bgColor} ${getProgressStatusDisplay(task.progressStatus).color}`}
                        >
                          {getProgressIcon(task.progressStatus)}
                          <span className="ml-1">
                            {getProgressStatusDisplay(task.progressStatus).label}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons - Only show if task is not completed */}
                {task.status !== 'completed' && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setShowUpdateForm(!showUpdateForm)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Update Progress
                    </button>

                    <button
                      onClick={handleMarkComplete}
                      disabled={isUpdating}
                      className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      {isUpdating ? 'Updating...' : 'Mark Complete'}
                    </button>
                  </div>
                )}

                {/* Completed Task Message */}
                {task.status === 'completed' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                      <p className="text-sm text-green-800 font-medium">
                        This task has been completed. You can view the progress history below.
                      </p>
                    </div>
                  </div>
                )}

                {/* Update Form - Only show if task is not completed */}
                {showUpdateForm && task.status !== 'completed' && (
                  <div className="bg-gray-50 rounded-lg p-3 space-y-3">
                    <h4 className="text-xs font-medium text-gray-900">Add Progress Update</h4>

                    {/* Progress Slider */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Progress: {updateForm.progress}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={updateForm.progress}
                        onChange={e =>
                          setUpdateForm(prev => ({ ...prev, progress: Number(e.target.value) }))
                        }
                        className="w-full h-1"
                      />
                      <div className="mt-1 bg-gray-200 rounded-full h-1">
                        <div
                          className="bg-red-500 h-1 rounded-full transition-all duration-300"
                          style={{ width: `${updateForm.progress}%` }}
                        />
                      </div>
                    </div>

                    {/* Status Dropdown */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={updateForm.status}
                        onChange={e => setUpdateForm(prev => ({ ...prev, status: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      >
                        {progressStatusOptions?.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        )) || <option value="on_track">On Track</option>}
                      </select>
                    </div>

                    {/* Details Text Area */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Progress Details
                      </label>
                      <textarea
                        value={updateForm.details}
                        onChange={e =>
                          setUpdateForm(prev => ({ ...prev, details: e.target.value }))
                        }
                        placeholder="Describe what was accomplished..."
                        rows={2}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                    </div>

                    {/* Form Actions */}
                    <div className="flex items-center justify-end space-x-2 pt-2">
                      <button
                        onClick={() => {
                          setShowUpdateForm(false);
                          setUpdateForm(prev => ({ ...prev, details: '' }));
                        }}
                        className="px-2 py-1 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleProgressUpdate}
                        disabled={isUpdating || !updateForm.details.trim()}
                        className="px-2 py-1 bg-red-500 text-white text-xs font-medium rounded-md hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                      >
                        {isUpdating ? 'Updating...' : 'Add Update'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Progress History */}
                <div className="space-y-3">
                  <h4 className="text-xs font-medium text-gray-900">
                    Progress History ({progressHistory.length} updates)
                  </h4>

                  {progressHistory.length > 0 ? (
                    <div className="space-y-2">
                      {progressHistory.map((update, index) => (
                        <div
                          key={update.id || index}
                          className="bg-white border border-gray-200 rounded-md p-3"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <span
                                  className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getProgressStatusDisplay(update.progressStatus || 'on_track').bgColor} ${getProgressStatusDisplay(update.progressStatus || 'on_track').color}`}
                                >
                                  {getProgressIcon(update.progressStatus || 'on_track')}
                                  <span className="ml-0.5">
                                    {
                                      getProgressStatusDisplay(update.progressStatus || 'on_track')
                                        .label
                                    }
                                  </span>
                                </span>
                                <span className="text-xs font-semibold text-gray-900">
                                  {update.progress}%
                                </span>
                              </div>

                              <p className="text-xs text-gray-700 mb-1">{update.details}</p>

                              <div className="flex items-center space-x-3 text-xs text-gray-500">
                                <span>By {getUserDisplayName(update.updatedBy)}</span>
                                <span>{formatDate(update.createdAt)}</span>
                              </div>
                            </div>

                            <div className="ml-3">
                              <div className="w-12 bg-gray-200 rounded-full h-1">
                                <div
                                  className="bg-red-500 h-1 rounded-full"
                                  style={{ width: `${update.progress}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <TrendingUp className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">
                        No progress updates yet
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Start tracking progress by adding your first update.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <p className="text-gray-500">Task not found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default TaskProgressDrawer;
